import { API_BASE } from './apiBase';
/**
 * First-party, anonymous analytics helper.
 *
 * • Generates a random session_id on first visit, persists in localStorage.
 *   No cookies, no IP collection, no fingerprinting.
 * • Exposes `track(type, payload)` — fires-and-forgets a POST to /api/events.
 *   Failures are silent; analytics must never break the user experience.
 * • In-memory de-bounce for page_view (one per pathname per minute).
 */

const STORAGE_KEY = 'sv_session_id';

function uuidv4() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // RFC4122 v4-ish fallback
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getSessionId() {
  try {
    let id = localStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = 'sess_' + uuidv4();
      localStorage.setItem(STORAGE_KEY, id);
    }
    return id;
  } catch {
    // Private mode / disabled storage — fall back to a volatile in-memory id.
    if (!window.__sv_session_id) window.__sv_session_id = 'sess_' + uuidv4();
    return window.__sv_session_id;
  }
}

const ALLOWED_TYPES = new Set([
  'card_click',
  'card_flip',
  'book_now_click',
  'more_info_click',
  'search_query',
  'category_filter_click',
  'language_change',
  'currency_change',
  'page_view',
]);

const CONSENT_KEY = 'sv_consent';
function hasConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY) === 'accepted';
  } catch {
    return false;
  }
}

const _pageViewCache = new Map(); // path -> last timestamp

/**
 * Fire an event. Never throws; never returns a promise you need to await.
 * Silently drops every event until the user accepts analytics cookies
 * via the CookieConsentBanner — required for GDPR/ePrivacy compliance.
 */
export function track(type, payload = {}) {
  if (!hasConsent()) return;
  if (!ALLOWED_TYPES.has(type)) {
    // Fast-fail in dev so typos are caught
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[analytics] unknown event type:', type);
    }
    return;
  }
  if (type === 'page_view') {
    const path = payload?.page || window.location.pathname;
    const last = _pageViewCache.get(path) || 0;
    if (Date.now() - last < 60_000) return; // de-bounce 1/min/path
    _pageViewCache.set(path, Date.now());
  }

  const body = JSON.stringify({
    type,
    session_id: getSessionId(),
    payload: payload || {},
  });

  try {
    // Prefer sendBeacon for fire-and-forget — survives page unload.
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      const ok = navigator.sendBeacon(`${API_BASE}/api/events`, blob);
      if (ok) return;
    }
    fetch(`${API_BASE}/api/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* swallow */
  }
}

/**
 * Decorate an affiliate URL with UTM params so partners (GetYourGuide,
 * Viator, Tiqets, GoCity) can attribute conversions back to SitgesVibes.
 * Falls back to the original URL on any parse error so a malformed value
 * never breaks the booking link.
 */
export function affiliateUrl(rawUrl, exp = {}) {
  if (!rawUrl) return rawUrl || '#';
  try {
    const url = new URL(rawUrl);
    if (!url.searchParams.has('utm_source')) url.searchParams.set('utm_source', 'sitgesvibes');
    if (!url.searchParams.has('utm_medium')) url.searchParams.set('utm_medium', 'referral');
    if (!url.searchParams.has('utm_campaign')) url.searchParams.set('utm_campaign', exp.id || 'experience');
    if (!url.searchParams.has('utm_content') && exp.affiliate_provider) {
      url.searchParams.set('utm_content', exp.affiliate_provider);
    }
    return url.toString();
  } catch {
    return rawUrl;
  }
}
